import React from 'react';
import { cn } from '../../../../lib/utils';
import {
  KPI_COLOR_OPTIONS,
  KPI_ICON_PRESETS,
  KPI_FORMAT_OPTIONS,
  KPI_SIZE_OPTIONS,
  KPI_SECTION_COLORS,
  emptyKpiCard,
  emptyKpiLine,
  normalizeKpiCard,
  applyNakedKpiChrome,
  isNakedKpiChrome,
} from '../../utils/kpiItems';

const inputCls = 'w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200';
const selectCls = inputCls;

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      {label && <label className="text-[10px] font-semibold text-slate-500">{label}</label>}
      {children}
    </div>
  );
}

function FormulaField({ label, value, onChange, fieldOptions, placeholder }) {
  return (
    <Field label={label}>
      <input
        className={cn(inputCls, 'font-mono dir-ltr text-left')}
        dir="ltr"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'مثال: (sale / target) * 100'}
      />
      {fieldOptions?.length > 0 && (
        <select
          className={cn(selectCls, 'mt-1')}
          value=""
          onChange={(e) => {
            const key = e.target.value;
            if (!key) return;
            const next = value && String(value).trim() ? `${value} ${key}` : key;
            onChange(next);
          }}
        >
          <option value="">+ درج فیلد کوئری</option>
          {fieldOptions.map((f) => (
            <option key={f.key || f} value={f.key || f}>
              {f.fa ? `${f.fa} (${f.key})` : (f.key || f)}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

function ColorChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1">
      {KPI_SECTION_COLORS.map((c) => (
        <button
          key={c.value || 'auto'}
          type="button"
          title={c.label}
          onClick={() => onChange(c.value)}
          className={cn(
            'h-6 min-w-[1.5rem] rounded-full border px-1.5 text-[9px]',
            (value || '') === c.value
              ? 'border-indigo-400 ring-1 ring-indigo-200'
              : 'border-slate-200',
          )}
          style={c.value ? { backgroundColor: c.value, color: '#fff' } : undefined}
        >
          {c.value ? '' : c.label}
        </button>
      ))}
      <input
        type="color"
        value={value || '#334155'}
        onChange={(e) => onChange(e.target.value)}
        className="h-6 w-7 cursor-pointer rounded border border-slate-200"
        title="رنگ دلخواه"
      />
    </div>
  );
}

function SectionBlock({ title, accent, section, fieldOptions, onChange, children }) {
  const patch = (p) => onChange({ ...section, ...p });
  return (
    <div className={cn('rounded-xl border bg-white p-2.5 space-y-2', accent)}>
      <p className="text-[10px] font-bold text-slate-600">{title}</p>
      {children}
      <FormulaField
        label="فرمول"
        value={section?.formula}
        onChange={(formula) => patch({ formula })}
        fieldOptions={fieldOptions}
      />
      <Field label="یا ستون کوئری">
        <select
          className={selectCls}
          value={section?.key || ''}
          onChange={(e) => patch({ key: e.target.value })}
        >
          <option value="">— بدون ستون —</option>
          {fieldOptions.map((f) => (
            <option key={f.key || f} value={f.key || f}>
              {f.fa ? `${f.fa} (${f.key})` : (f.key || f)}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="نوع نمایش">
          <select
            className={selectCls}
            value={section?.format || 'number'}
            onChange={(e) => patch({ format: e.target.value })}
          >
            {KPI_FORMAT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </Field>
        <Field label="اندازه">
          <select
            className={selectCls}
            value={section?.size || 'md'}
            onChange={(e) => patch({ size: e.target.value })}
          >
            {KPI_SIZE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>
        <Field label="اعشار">
          <input
            type="number"
            min={0}
            max={6}
            className={inputCls}
            value={section?.decimals ?? 0}
            onChange={(e) => patch({ decimals: Number(e.target.value) })}
          />
        </Field>
      </div>
      <Field label="رنگ متن">
        <ColorChips value={section?.color || ''} onChange={(color) => patch({ color })} />
      </Field>
      <FormulaField
        label="نوار پیشرفت (۰ تا ۱۰۰)"
        value={section?.progressFormula}
        onChange={(progressFormula) => patch({ progressFormula })}
        fieldOptions={fieldOptions}
        placeholder="(actual / target) * 100"
      />
      {section?.progressFormula ? (
        <Field label="رنگ نوار">
          <ColorChips value={section?.progressColor || ''} onChange={(progressColor) => patch({ progressColor })} />
        </Field>
      ) : null}
    </div>
  );
}

export default function KpiCardEditor({ widget, fieldOptions, catalogFields, onChange }) {
  const raw = widget?.statConfig || {};
  const legacyCount = Array.isArray(raw.items) ? raw.items.length : 0;
  const card = Object.keys(raw).length ? normalizeKpiCard(raw) : emptyKpiCard();
  const formulaFields = (catalogFields || fieldOptions || []).map((f) => (
    typeof f === 'string' ? { key: f, fa: f } : f
  ));

  const patch = (p) => onChange({ ...card, ...p });

  return (
    <div className="space-y-3">
      {legacyCount > 1 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[10px] leading-relaxed text-amber-800">
          این ویجت قبلاً چند کارت در یک شبکه بود. از این به بعد یک کارت است؛ برای چند شاخص، چند ویجت KPI جدا بگذارید.
        </p>
      )}

      <Field label="آیکون">
        <div className="flex flex-wrap gap-1">
          {KPI_ICON_PRESETS.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => patch({ icon })}
              className={cn(
                'h-8 w-8 rounded-lg border text-sm',
                card.icon === icon ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              {icon}
            </button>
          ))}
        </div>
        <input
          className={cn(inputCls, 'mt-1')}
          value={card.icon || ''}
          onChange={(e) => patch({ icon: e.target.value })}
          placeholder="ایموجی دلخواه"
        />
      </Field>

      <Field label="رنگ کارت">
        <div className="flex flex-wrap gap-1">
          {KPI_COLOR_OPTIONS.map((c) => (
            <button
              key={c.value}
              type="button"
              title={c.label}
              onClick={() => patch({ color: c.value })}
              className={cn(
                'rounded-full border px-2 py-0.5 text-[10px]',
                card.color === c.value
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-800'
                  : 'border-slate-200 text-slate-600',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      </Field>

      <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-2 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-bold text-slate-500">قاب کارت</p>
          <button
            type="button"
            className={cn(
              'rounded-full border px-2 py-0.5 text-[10px] font-bold',
              isNakedKpiChrome(card)
                ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50',
            )}
            onClick={() => patch(applyNakedKpiChrome(card, !isNakedKpiChrome(card)))}
          >
            {isNakedKpiChrome(card) ? 'با قاب' : 'بدون قاب'}
          </button>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <input type="checkbox" className="accent-indigo-600" checked={card.showFrame !== false}
            onChange={(e) => patch({ showFrame: e.target.checked })} />
          حاشیه و سایه
        </label>
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <input type="checkbox" className="accent-indigo-600" checked={card.showTitle !== false}
            onChange={(e) => patch({ showTitle: e.target.checked })} />
          هدر (آیکون و عنوان)
        </label>
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <input type="checkbox" className="accent-indigo-600" checked={card.padded !== false}
            onChange={(e) => patch({ padded: e.target.checked })} />
          فاصله داخلی
        </label>
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <input type="checkbox" className="accent-indigo-600" checked={!!card.fitContent}
            onChange={(e) => patch({ fitContent: e.target.checked })} />
          اندازه متناسب با محتوا
        </label>
      </div>

      <SectionBlock
        title="مقدار اصلی"
        accent="border-indigo-100"
        section={card.value}
        fieldOptions={formulaFields}
        onChange={(value) => patch({ value })}
      />

      <div className="grid grid-cols-2 gap-2">
        <SectionBlock
          title="زیرنویس راست"
          accent="border-slate-100"
          section={card.subtitleLeft || card.subtitle}
          fieldOptions={formulaFields}
          onChange={(subtitleLeft) => patch({ subtitleLeft })}
        >
          <Field label="برچسب">
            <input
              className={inputCls}
              value={(card.subtitleLeft || card.subtitle)?.label || ''}
              onChange={(e) => patch({ subtitleLeft: { ...(card.subtitleLeft || card.subtitle), label: e.target.value } })}
              placeholder="فروش"
            />
          </Field>
        </SectionBlock>
        <SectionBlock
          title="زیرنویس چپ"
          accent="border-slate-100"
          section={card.subtitleRight}
          fieldOptions={formulaFields}
          onChange={(subtitleRight) => patch({ subtitleRight })}
        >
          <Field label="برچسب">
            <input
              className={inputCls}
              value={card.subtitleRight?.label || ''}
              onChange={(e) => patch({ subtitleRight: { ...card.subtitleRight, label: e.target.value } })}
              placeholder="هدف"
            />
          </Field>
        </SectionBlock>
      </div>

      <SectionBlock
        title="بج"
        accent="border-slate-100"
        section={card.badge}
        fieldOptions={formulaFields}
        onChange={(badge) => patch({ badge })}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold text-slate-500">خطوط اضافه</p>
          <button
            type="button"
            className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700"
            onClick={() => patch({ lines: [...(card.lines || []), emptyKpiLine(card.lines)] })}
          >
            + خط
          </button>
        </div>
        {(card.lines || []).map((line, i) => (
          <div key={line.id} className="rounded-xl border border-slate-100 p-2 space-y-2">
            <div className="flex gap-1">
              <input
                className={inputCls}
                value={line.label || ''}
                placeholder="برچسب خط"
                onChange={(e) => {
                  const next = card.lines.map((l, idx) => (idx === i ? { ...l, label: e.target.value } : l));
                  patch({ lines: next });
                }}
              />
              <button
                type="button"
                className="shrink-0 text-[10px] text-rose-500"
                onClick={() => patch({ lines: card.lines.filter((_, idx) => idx !== i) })}
              >
                حذف
              </button>
            </div>
            <SectionBlock
              title=""
              accent="border-transparent p-0"
              section={line}
              fieldOptions={formulaFields}
              onChange={(nextLine) => {
                const next = card.lines.map((l, idx) => (idx === i ? { ...l, ...nextLine, id: l.id } : l));
                patch({ lines: next });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
